import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Inbox, Sprout, ScrollText, ChevronRight, ImageOff, Briefcase, Bell, Check, PartyPopper } from "lucide-react";
import { useListings } from "@/hooks/useListings.js";
import { useInquiries } from "@/hooks/useInquiries.js";
import { useAuditLogs } from "@/hooks/useAuditLogs.js";
import { useRealtimeInquiries } from "@/hooks/useRealtimeInquiries.js";
import { useAttention, useDismissAttention, type AttentionItem } from "@/hooks/useAttention.js";
import { useAuth } from "@/auth/useAuth.js";

function getTitleIt(title: unknown): string {
  if (title && typeof title === "object" && "it" in title) return String((title as { it: unknown }).it ?? "");
  return "";
}
const fmtPrice = (n: number) => "€ " + n.toLocaleString("it-IT");

export default function Dashboard(): JSX.Element {
  useRealtimeInquiries();
  const { data: listings } = useListings({ page_size: 50 });
  const { data: inquiriesNew } = useInquiries({ status: "nuovo" });
  const { data: audit } = useAuditLogs({ limit: 6 });

  const items = listings?.items ?? [];
  const total = listings?.total ?? 0;
  const published = items.filter((i) => i.published_snapshot_id != null).length;
  const value = items.reduce((s, i) => s + (i.prezzo ?? 0), 0);
  const leads = inquiriesNew?.total ?? 0;
  const recent = (inquiriesNew?.items ?? []).slice(0, 5);

  return (
    <div className="space-y-[22px]">
      <header>
        <h1 className="font-display text-[27px] font-bold tracking-tight text-ink">Dashboard</h1>
        <p className="mt-[5px] text-[13.5px] text-ink-soft">Panoramica archivio av/estate.</p>
      </header>

      {/* stat strip */}
      <div className="grid grid-cols-2 overflow-hidden rounded-[5px] border border-rule bg-surface sm:grid-cols-4">
        <Stat v={String(total)} l="Immobili" delta="+2 mese" />
        <Stat v={String(published)} l="Pubblicati" />
        <Stat v={String(leads)} l="Lead aperti" />
        <Stat v={`€${(value / 1e6).toFixed(2).replace(".", ",")}`} unit="M" l="Valore archivio" last />
      </div>

      <AttentionPanel />

      <div className="grid grid-cols-1 gap-[18px] lg:grid-cols-2">
        {/* collections */}
        <Panel ticks title="Collections" meta="navigazione">
          <CollCard to="/listings" Icon={Building2} name="Immobili" sub="archivio principale" big={String(total)} />
          <CollCard to="/inbox" Icon={Inbox} name="Inbox lead" sub="richieste in arrivo" big={String(leads)} border />
          <CollCard to="/inbox?source=seller" Icon={Sprout} name="Vendi casa" sub="lead valutazione" border />
          <CollCard to="/audit" Icon={ScrollText} name="Audit log" sub={`${audit?.items.length ?? 0} eventi recenti`} border />
        </Panel>

        {/* recent activity */}
        <Panel title="Attività recente" meta="live">
          {recent.length === 0 ? (
            <p className="p-[18px] text-[13px] text-ink-mute">Nessuna richiesta nuova.</p>
          ) : (
            <div className="py-[6px]">
              {recent.map((i) => (
                <div key={i.id} className="flex items-center gap-[12px] border-b border-rule-soft p-[11px_18px] last:border-b-0">
                  <span className="size-[7px] shrink-0 rounded-full bg-accent" />
                  <span className="min-w-0 text-[13px]">
                    <b className="font-semibold">{i.name}</b>
                    <span className="text-ink-soft"> · {i.reason}</span>
                  </span>
                  <span className="ml-auto truncate font-mono text-[11px] text-ink-mute">{i.email}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {/* ultimi immobili */}
      <Panel ticks title="Ultimi immobili" action={<Link to="/listings" className="micro hover:text-accent">vedi tutti →</Link>}>
        <table className="w-full border-collapse">
          <tbody>
            {items.slice(0, 5).map((r) => {
              const title = getTitleIt(r.title);
              return (
                <tr key={r.id} className="border-b border-rule-soft transition-colors last:border-b-0 hover:bg-surface-2">
                  <td className="p-[12px_18px]">
                    <Link to={`/listings/${r.id}`} className="group flex items-center gap-[13px]">
                      {r.cover_url ? (
                        <img src={r.cover_url} alt="" loading="lazy" className="h-[40px] w-[54px] flex-none rounded-[5px] border border-rule object-cover" />
                      ) : (
                        <span className="grid h-[40px] w-[54px] flex-none place-items-center rounded-[5px] border border-rule bg-surface-2 text-ink-mute"><ImageOff className="size-4" /></span>
                      )}
                      <span className="min-w-0">
                        <span className="block truncate text-[13.5px] font-semibold text-ink group-hover:text-accent">{title || r.slug}</span>
                        <span className="block truncate font-mono text-[11px] text-ink-mute">{r.slug}</span>
                      </span>
                    </Link>
                  </td>
                  <td className="p-[12px_18px]">
                    <span className="inline-flex h-[24px] items-center gap-[7px] rounded-full border border-rule bg-surface-2 px-[10px] text-[11.5px] text-ink-soft">
                      <span className={`size-[6px] rounded-full ${r.published_snapshot_id != null ? "bg-success" : "bg-ink-mute"}`} />
                      {r.published_snapshot_id != null ? "Pubblicato" : "Bozza"}
                    </span>
                  </td>
                  <td className="whitespace-nowrap p-[12px_18px] text-[13px] text-ink-soft">{r.city ?? "—"}</td>
                  <td className="whitespace-nowrap p-[12px_18px] text-right font-mono text-[13.5px] font-semibold text-ink">
                    {r.prezzo != null ? fmtPrice(r.prezzo) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}

const KIND_META: Record<AttentionItem["kind"], { Icon: typeof Inbox; to: string; label: string }> = {
  lead: { Icon: Inbox, to: "/inbox", label: "Lead" },
  deal: { Icon: Briefcase, to: "/deals", label: "Proposta" },
  alert: { Icon: Bell, to: "/alerts", label: "Alert" },
};

function AttentionPanel(): JSX.Element {
  const { role } = useAuth();
  const canEdit = role === "owner" || role === "admin" || role === "editor";
  const [days, setDays] = useState(3);
  const { data, isLoading, error } = useAttention(days);
  const dismiss = useDismissAttention();
  const items = data?.items ?? [];

  const handle = (item: AttentionItem) => {
    dismiss.mutate({ kind: item.kind, id: item.id, source: item.source ?? undefined }, {
      onSuccess: () => toast.success("Segnato come gestito: riappare tra 7 giorni se resta fermo"),
      onError: (e) => toast.error(e instanceof Error ? e.message : "Operazione non riuscita"),
    });
  };

  return (
    <Panel
      ticks
      title="Nessuno resta indietro"
      action={
        <span className="flex items-center gap-[10px]">
          {items.length > 0 ? <span className="micro text-risk">{items.length} da seguire</span> : null}
          <label className="flex items-center gap-[6px] text-[11.5px] text-ink-mute">
            lead fermi da
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              aria-label="Soglia giorni lead"
              className="h-[24px] cursor-pointer rounded-[5px] border border-rule bg-surface-2 px-1 font-mono text-[11px] text-ink outline-none [&>option]:bg-surface [&>option]:text-ink"
            >
              {[1, 3, 5, 7, 14].map((d) => <option key={d} value={d}>{d} gg</option>)}
            </select>
          </label>
        </span>
      }
    >
      {isLoading ? (
        <p className="p-[18px] text-[13px] text-ink-mute">Carico…</p>
      ) : error ? (
        <p className="p-[18px] text-[13px] text-risk">Errore nel caricamento: {(error as Error).message}</p>
      ) : items.length === 0 ? (
        <div className="flex items-center gap-3 p-[18px]">
          <PartyPopper className="size-[18px] text-success" />
          <p className="text-[13px] text-ink-soft">Tutto seguito: nessun lead fermo, nessuna proposta in attesa, nessun match da proporre.</p>
        </div>
      ) : (
        <div className="py-[4px]">
          {items.slice(0, 12).map((item) => {
            const meta = KIND_META[item.kind];
            return (
              <div key={`${item.kind}-${item.id}`} className="flex items-center gap-[12px] border-b border-rule-soft p-[10px_18px] last:border-b-0">
                <span className={`inline-flex h-[22px] w-[52px] items-center justify-center rounded-full border font-mono text-[10.5px] ${item.waiting_days >= 7 ? "border-risk/50 bg-risk/10 text-risk" : item.waiting_days >= 3 ? "border-warn/50 bg-warn/10 text-warn" : "border-rule bg-surface-2 text-ink-soft"}`}>
                  {item.waiting_days} gg
                </span>
                <meta.Icon className="size-[15px] shrink-0 text-ink-mute" />
                <Link to={meta.to} className="min-w-0 flex-1 truncate text-[13px] hover:text-accent">
                  <b className="font-semibold">{item.title}</b>
                  <span className="text-ink-soft"> · {item.detail}</span>
                </Link>
                {canEdit ? (
                  <button
                    type="button"
                    disabled={dismiss.isPending}
                    onClick={() => handle(item)}
                    className="inline-flex h-[26px] items-center gap-[6px] whitespace-nowrap rounded-[5px] border border-rule bg-surface-2 px-[9px] text-[12px] text-ink-soft transition-colors hover:border-success/60 hover:text-success disabled:opacity-60"
                  >
                    <Check className="size-[13px]" /> Segna gestito
                  </button>
                ) : null}
              </div>
            );
          })}
          {items.length > 12 ? (
            <p className="p-[10px_18px] text-[11.5px] text-ink-mute">e altri {items.length - 12}…</p>
          ) : null}
        </div>
      )}
    </Panel>
  );
}

function Stat({ v, unit, l, delta, last }: { v: string; unit?: string; l: string; delta?: string; last?: boolean }): JSX.Element {
  return (
    <div className={`p-[16px_18px] ${last ? "" : "border-r border-rule-soft"}`}>
      <div className="font-mono text-[25px] font-semibold tracking-tight text-ink">{v}{unit ? <span className="text-[14px] font-medium text-ink-mute">{unit}</span> : null}</div>
      <div className="micro mt-[5px]">{l}</div>
      {delta ? <div className="mt-[7px] text-[11px] text-success">↗ {delta}</div> : null}
    </div>
  );
}

function Panel({ title, meta, action, ticks, children }: { title: string; meta?: string; action?: React.ReactNode; ticks?: boolean; children: React.ReactNode }): JSX.Element {
  return (
    <div className={`relative rounded-[5px] border border-rule bg-surface ${ticks ? "ticks" : ""}`}>
      <div className="flex items-center gap-[10px] border-b border-rule p-[13px_16px]">
        <h3 className="text-[13px] font-semibold">{title}</h3>
        {meta ? <span className="micro ml-auto">{meta}</span> : null}
        {action ? <span className="ml-auto">{action}</span> : null}
      </div>
      {children}
    </div>
  );
}

function CollCard({ to, Icon, name, sub, big, border }: { to: string; Icon: typeof Building2; name: string; sub: string; big?: string; border?: boolean }): JSX.Element {
  return (
    <Link to={to} className={`flex items-center gap-[14px] p-[16px_18px] ${border ? "border-t border-rule-soft" : ""} group`}>
      <span className="grid size-[38px] place-items-center rounded-[8px] border border-rule bg-surface-2 text-accent"><Icon className="size-[18px]" /></span>
      <span className="min-w-0">
        <span className="block text-[14px] font-semibold group-hover:text-accent">{name}</span>
        <span className="block font-mono text-[11px] text-ink-mute">{sub}</span>
      </span>
      {big ? <span className="ml-auto font-mono text-[22px] font-semibold">{big}</span> : <ChevronRight className="ml-auto size-4 text-ink-mute" />}
    </Link>
  );
}
